import { Card, Table, TableBody, TableRow, TableCell, Chip, Box, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { DataRow } from 'app/components';
import clsx from 'clsx';
import React, { useEffect } from 'react';
import TitleField from '../TitleField';

const useStyles = makeStyles((theme) => ({
  card: {
    padding: theme.spacing(2),
    width: '100%',
  },
  chip: {
    margin: theme.spacing(0.5),
    maxWidth: 400,
    height: 'auto',
    '& .MuiChip-label': {
      whiteSpace: 'normal',
      wordBreak: 'break-word',
    },
  },
  section: {
    marginTop: theme.spacing(1),
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: '18px',
    lineHeight: 1.5,
    marginBottom: theme.spacing(1),
  },
  chipRow: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  labelCell: {
    fontWeight: 'bold',
    verticalAlign: 'top',
  },
  valueCell: {
    padding: '8px',
  },
  legend: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    fontSize: 12,
  },
  legendDot: {
    width: 12,
    height: 12,
    borderRadius: '50%',
  },
  riskTable: {
    '& .MuiTableCell-root': {
      paddingLeft: 0,
      paddingRight: 0,
    },
  },

  riskLabelCell: {
    fontWeight: 'bold',
    verticalAlign: 'top',
    width: '30%',
    paddingLeft: '0 !important',
  },

  riskValueCell: {
    paddingRight: '0 !important',
  },
}));

const RegtankRiskTable = ({ kyc = {}, risk = {}, opticalStatus = {}, type, className }) => {
  const classes = useStyles();

  const normalizedType = (type || '').toLowerCase();

  const hasKyc = kyc && Object.keys(kyc).length > 0;
  const hasRisk = risk && Object.keys(risk).length > 0;

  const riskTags = Array.isArray(risk?.riskTags) ? risk.riskTags : [];

  // 🔥 group risk tags
  const typeGroups = {
    DETECT: riskTags.filter((t) => t.type === 'DETECT'),
    COMPARE: riskTags.filter((t) => t.type === 'COMPARE'),
    AUTHENTICATE: riskTags.filter((t) => t.type === 'AUTHENTICATE'),
    ANALYSE: riskTags.filter((t) => t.type === 'ANALYSE'),
  };

  const getChipStyle = (icon) => {
    if (icon === 'GREEN') return { backgroundColor: '#70d602', color: '#000' };
    if (icon === 'YELLOW') return { backgroundColor: '#ffc15d', color: '#000' };
    if (icon === 'RED') return { backgroundColor: '#fdecea', color: 'red' };
    return { backgroundColor: '#e0e0e0', color: '#000' };
  };

  const typeLabels = {
    DETECT: 'Detection',
    COMPARE: 'Comparison',
    AUTHENTICATE: 'Authentication',
    ANALYSE: 'Analyse',
  };

  const getStatusColor = (value) => {
    if (value === 'OK') return { backgroundColor: '#70d602', color: '#000' };
    if (value === 'ERROR') return { backgroundColor: '#fdecea', color: 'red' };
    if (value === 'WAS_NOT_DONE') return { backgroundColor: '#e0e0e0', color: '#000' };
    return { backgroundColor: '#e0e0e0', color: '#000' };
  };

  const formatLabel = (key) => key.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase());

  return (
    <Card className={clsx(classes.card, className)}>
      {normalizedType === 'upload' && (
      <>
        {/* ================= DOCUMENT VALIDATION ================= */}
        <Box className={classes.section}>
          <Typography className={classes.sectionTitle}>
            Document Validation
          </Typography>

          {Object.keys(opticalStatus).length === 0 ? (
            <Typography
              variant="body2"
              color="textSecondary"
              style={{ paddingTop: 8, paddingBottom: 8 }}
            >
              No validation data available yet.
            </Typography>
          ) : (
            Object.entries(opticalStatus).map(([key, value]) => (
              <Box
                key={key}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                py={1}
                borderBottom="1px solid #eee"
              >
                <Typography className={classes.labelCell}>
                  {formatLabel(key)}
                </Typography>

                <Chip
                  label={
                    value === 'OK'
                      ? 'Passed'
                      : value === 'ERROR'
                      ? 'Failed'
                      : value === 'WAS_NOT_DONE'
                      ? 'Not Checked'
                      : value
                  }
                  size="small"
                  style={getStatusColor(value)}
                />
              </Box>
            ))
          )}
        </Box>

        {/* ================= RISK SUMMARY ================= */}
        <Box
          className={classes.section}
          style={{ marginTop: 16 }}
        >
          <Typography className={classes.sectionTitle}>
            Risk Summary
          </Typography>

          {!hasRisk || riskTags.length === 0 ? (
            <Typography
              variant="body2"
              color="textSecondary"
              style={{ paddingTop: 8, paddingBottom: 8 }}
            >
              No risk detected.
            </Typography>
          ) : (
            <>
              {/* Risk Count */}
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                py={1}
                borderBottom="1px solid #eee"
              >
                <Typography className={classes.labelCell}>
                  Risk Count
                </Typography>

                <Typography>
                  {riskTags.length}
                </Typography>
              </Box>

              {/* Risk Flags */}
              <Box
                display="flex"
                alignItems="flex-start"
                py={1}
                borderBottom="1px solid #eee"
              >
                <Box
                  style={{
                    width: '30%',
                    flexShrink: 0,
                  }}
                >
                  <Typography className={classes.labelCell}>
                    Risk Flags
                  </Typography>
                </Box>

                <Box style={{ flex: 1 }}>
                  {/* Legend */}
                  <Box className={classes.legend}>
                    <Box className={classes.legendItem}>
                      <Box
                        className={classes.legendDot}
                        style={{ backgroundColor: '#70d602' }}
                      />
                      <Typography variant="caption">
                        Passed
                      </Typography>
                    </Box>

                    <Box className={classes.legendItem}>
                      <Box
                        className={classes.legendDot}
                        style={{ backgroundColor: '#ffc15d' }}
                      />
                      <Typography variant="caption">
                        Warning
                      </Typography>
                    </Box>

                    <Box className={classes.legendItem}>
                      <Box
                        className={classes.legendDot}
                        style={{
                          backgroundColor: '#fdecea',
                          border: '1px solid red',
                        }}
                      />
                      <Typography variant="caption">
                        Critical
                      </Typography>
                    </Box>
                  </Box>

                  {/* Risk Groups */}
                  {Object.entries(typeGroups).map(([riskType, tags]) => {
                    if (tags.length === 0) return null;

                    return (
                      <Box
                        key={riskType}
                        className={classes.section}
                      >
                        <Typography className={classes.sectionTitle}>
                          {typeLabels[riskType]}
                        </Typography>

                        <Box className={classes.chipRow}>
                          {tags.map((tag, index) => (
                            <Chip
                              key={`${riskType}-${index}`}
                              label={tag.text}
                              size="small"
                              className={classes.chip}
                              style={getChipStyle(tag.icon)}
                            />
                          ))}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </>
          )}
        </Box>
      </>
    )}


      {/* ✅ NORMAL FLOW */}
      {normalizedType !== 'upload' && (
        <>
          <TitleField>KYC & Risk Summary</TitleField>
          {/* ❌ no data */}
          {!hasKyc && !hasRisk && (
            <Box p={2}>
              <Typography variant="body2" color="textSecondary">
                No KYC or risk data available.
              </Typography>
            </Box>
          )}

          {/* ✅ show table */}
          {(hasKyc || hasRisk) && (
            <Table>
              <TableBody>
                {/* 🔹 KYC */}
                {hasKyc && (
                  <>
                    <DataRow name="KYC Match" value={kyc.kycPositiveMatch ? 'Yes' : 'No'} />
                    <DataRow name="KYC Type" value={kyc.kycType || '-'} />
                  </>
                )}

                {/* 🔹 Risk Count */}
                {hasRisk && <DataRow name="Risk Count" value={riskTags.length} />}

                {/* 🔹 Risk Flags */}
                {hasRisk && riskTags.length > 0 && (
                  <TableRow>
                    <TableCell className={classes.labelCell}>Risk Flags</TableCell>

                    <TableCell className={classes.valueCell}>
                      {/* Legend */}
                      <Box className={classes.legend}>
                        <Box className={classes.legendItem}>
                          <Box className={classes.legendDot} style={{ backgroundColor: '#70d602' }} />
                          <Typography variant="caption">Passed</Typography>
                        </Box>

                        <Box className={classes.legendItem}>
                          <Box className={classes.legendDot} style={{ backgroundColor: '#ffc15d' }} />
                          <Typography variant="caption">Warning</Typography>
                        </Box>

                        <Box className={classes.legendItem}>
                          <Box
                            className={classes.legendDot}
                            style={{
                              backgroundColor: '#fdecea',
                              border: '1px solid red',
                            }}
                          />
                          <Typography variant="caption">Critical</Typography>
                        </Box>
                      </Box>

                      {/* grouped chips */}
                      {Object.entries(typeGroups).map(([type, tags]) =>
                        tags.length > 0 ? (
                          <Box key={type} className={classes.section}>
                            <Typography className={classes.sectionTitle}>{typeLabels[type]}</Typography>

                            <Box className={classes.chipRow}>
                              {tags.map((tag, index) => (
                                <Chip key={index} label={tag.text} size="small" className={classes.chip} style={getChipStyle(tag.icon)} />
                              ))}
                            </Box>
                          </Box>
                        ) : null
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </>
      )}
    </Card>
  );
};

export default RegtankRiskTable;
